import React, { Key, useEffect, useState } from 'react'
import './LoanAdmin.scss'
import { Link, useNavigate } from 'react-router-dom'
import { Logger } from '../../../../../Helpers/Logger'
import { HorizontalLoader } from '../../../../../Components'
import Pagination from '../../../../../Components/Pagination/Pagination'
import { CurrencyFormatter, TableSerialNumber } from 'Helpers'
import loanService from 'Services/loanServices'
import { loanType } from 'Models/loanType'
import { TimeConverter } from 'Helpers/TimeConverter'
import { useForm } from 'react-hook-form'
import { FilterSvg, DownloadSvg, SearchSvgMuted } from 'Components/Svgs'

const LoanAdmin = () => {
  const [pageNumber, setPage] = useState(1)

  const [loan, setLoan] = useState<loanType[]>([])
  const [loading, setLoading] = useState(false)

  const { register, watch } = useForm()
  const [debouncedEmployeeName, setDebouncedEmployeeName] = useState('')
  const employeeName = watch('employeeName')
  const [sortOrder, setSortOrder] = useState('DESC')
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [financeApprovalStatus, setFinanceApprovalStatus] = useState<string | undefined>(undefined)
  const [isRepaymentComplete, setIsRepaymentComplete] = useState<string | undefined>(undefined)

  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  })

  const buildSearchParams = (value?: string) => {
    if (!value) return {}
    const trimmed = value.trim()

    if (trimmed.startsWith('EMP_')) {
      return { userId: trimmed }
    }
    if (/^\d+$/.test(trimmed)) {
      return { id: trimmed }
    }
    if (/^[a-zA-Z0-9]+$/.test(trimmed) && trimmed.length > 15) {
      return { id: trimmed }
    }

    return { employeeName: trimmed }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedEmployeeName(employeeName || '')
    }, 500)

    return () => clearTimeout(timeout)
  }, [employeeName])

  const getLoan = async () => {
    setLoading(true)
    const params = buildSearchParams(debouncedEmployeeName)

    await loanService
      .getAll(pageNumber, sortOrder, status, financeApprovalStatus, isRepaymentComplete, params)
      .then(
        (response: {
          data: {
            data: {
              dataset: Array<loanType>
              pageNumber: any
              pageSize: any
              total: any
            }
          }
        }) => {
          setLoading(false)
          setLoan(response.data.data.dataset)
          console.log('getLoan', response)

          setPaginationData({
            pageNumber: response?.data.data.pageNumber,
            pageSize: response?.data.data.pageSize,
            total: response?.data.data.total,
          })
        },
        (error: { response: object }) => {
          setLoading(false)
          Logger('getLoan error', error.response)
        },
      )
  }

  useEffect(() => {
    getLoan()
  }, [
    pageNumber,
    sortOrder,
    status,
    financeApprovalStatus,
    isRepaymentComplete,
    debouncedEmployeeName,
  ])

  const handleReset = (e: any) => {
    e.preventDefault()
    setStatus(undefined)
    setFinanceApprovalStatus(undefined)
    setIsRepaymentComplete(undefined)
  }

  const empty = status || financeApprovalStatus || isRepaymentComplete

  const navigate = useNavigate()
  return (
    <div className='LoanAdmin'>
      <div className='container mt-4'>
        <div className='container'>
          <div className='card border-0'>
            <div className='card-header border-0 px-0'>
              <div className='row'>
                <div className='col-6 col-md-6 my-auto'>
                  <div className='title'>{paginationData.total} Loan Applications</div>
                </div>
                <div className='col-6 col-md-6 text-end'></div>
              </div>

              <div className='row mt-2'>
                <div className='col-12 col-md-8'>
                  <div className='search-field'>
                    <span>
                      <SearchSvgMuted />
                    </span>

                    <input
                      type='text'
                      className='form-control'
                      placeholder='Search Loan Id, Employee ID or Employee Name'
                      {...register('employeeName')}
                    />
                  </div>
                </div>
                <div className='col-6 col-md-2 dropdown'>
                  <button
                    className='btn btn-filter dropdown-toggle'
                    data-bs-toggle='dropdown'
                    aria-expanded='false'
                  >
                    <FilterSvg />
                    Filters
                  </button>
                  <div className='dropdown-menu dropdown-menu-end border-0 shadow filter-dropdown'>
                    <div className='dropdown-item'>
                      <form action=''>
                        <div className='form-group'>
                          <label htmlFor=''>Sort List</label>
                          <select
                            id=''
                            className='form-control'
                            onChange={(e: any) => {
                              setSortOrder(e.target.value)
                            }}
                          >
                            <option value='DESC'>Descending Order</option>
                            <option value='ASC'>Ascending Order</option>
                          </select>
                        </div>
                        <div className='form-group mt-3'>
                          <label htmlFor=''>Status</label>
                          <select
                            id=''
                            value={status || ''}
                            className='form-control'
                            onChange={(e: any) => {
                              setStatus(e.target.value || undefined)
                            }}
                          >
                            <option disabled value=''>
                              ---select---
                            </option>
                            <option value='PENDING_APPROVAL'>Pending Approval</option>
                            <option value='APPROVED'>Approved</option>
                            <option value='REJECTED'>Rejected</option>
                            <option value='COMPLETED_REPAYMENT'>Completed Repayment</option>
                          </select>
                        </div>
                        <div className='form-group mt-3'>
                          <label htmlFor=''>Finance Status</label>
                          <select
                            id=''
                            value={financeApprovalStatus || ''}
                            className='form-control'
                            onChange={(e: any) => {
                              setFinanceApprovalStatus(e.target.value || undefined)
                            }}
                          >
                            <option disabled value=''>
                              ---select---
                            </option>
                            <option value='PENDING_FINANCE_APPROVAL'>
                              Pending Finance Approval
                            </option>
                            <option value='APPROVED'>Approved</option>
                            <option value='REJECTED'>Rejected</option>
                          </select>
                        </div>
                        <div className='form-group mt-3'>
                          <label htmlFor=''>Repayment Status</label>
                          <select
                            id=''
                            value={isRepaymentComplete || ''}
                            className='form-control'
                            onChange={(e: any) => {
                              setIsRepaymentComplete(e.target.value || undefined)
                            }}
                          >
                            <option disabled value=''>
                              ---select---
                            </option>
                            <option value='true'>Yes</option>
                            <option value='false'>No</option>
                          </select>
                        </div>

                        <div className='row mt-3'>
                          <div className='col-md-6'>
                            <button
                              disabled={!empty}
                              onClick={handleReset}
                              className='btn btn-reset'
                            >
                              Reset
                            </button>
                          </div>
                          <div className='col-md-6 text-end d-none'>
                            <button className='btn btn-filter'>Filter</button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
                <div className='col-6 col-md-2 dropdown'>
                  <button
                    className='btn btn-export dropdown-toggle'
                    data-bs-toggle='dropdown'
                    aria-expanded='false'
                  >
                    <DownloadSvg />
                    Export
                  </button>

                  <ul className='dropdown-menu dropdown-menu-end export-dropdown border-0 shadow'>
                    <li className='dropdown-item'> Export as .pdf</li>
                    <li className='dropdown-item'> Export as .xlsx</li>
                    <li className='dropdown-item'> Export as .doc </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className='card-body'>
              <div className='table-responsive'>
                <table className='table'>
                  <thead>
                    <tr>
                      <td>#</td>
                      <td>Employee Name</td>
                      <td>Loan Amount</td>
                      <td>Monthly Repayment</td>
                      <td> Repayment Duration</td>
                      <td>Status</td>
                      <td>App. Date</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.length > 0 &&
                      loan.map((data: loanType, index: Key) => (
                        <tr
                          key={index}
                          onClick={() => {
                            navigate(`/admin/loan/${data.id}`)
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            {TableSerialNumber(
                              pageNumber,
                              paginationData.pageSize,
                              parseInt(index.toString()),
                            )}
                          </td>
                          <td>
                            {data.user.firstName} {data.user.lastName}
                          </td>
                          <td>₦{CurrencyFormatter(data.amount)}</td>
                          <td>₦{CurrencyFormatter(Number(data.monthlyRepaymentAmount))}</td>

                          <td>{data.repaymentDurationInMonths ?? 0} Months</td>
                          {/* <td
                            className={
                              data.status === 'PENDING_APPROVAL'
                                ? 'text-danger fw-bold'
                                : 'text-success fw-bold'
                            }
                          >
                            {data.status}
                          </td> */}
                          <td
                            className={
                              data.status === 'REJECTED'
                                ? 'text-danger fw-bold'
                                : data.status === 'COMPLETED_REPAYMENT'
                                  ? 'text-success fw-bold'
                                  : data.financeApprovalStatus === 'PENDING_FINANCE_APPROVAL' ||
                                      data.status === 'PENDING_APPROVAL'
                                    ? 'text-orange fw-bold'
                                    : 'text-success fw-bold'
                            }
                          >
                            {data.status === 'REJECTED'
                              ? 'Rejected'
                              : data.status === 'COMPLETED_REPAYMENT'
                                ? 'Completed Repayment'
                                : data.financeApprovalStatus === 'PENDING_FINANCE_APPROVAL'
                                  ? 'Pending Finance Approval'
                                  : data.financeApprovalStatus === 'APPROVED' &&
                                      data.status === 'PENDING_APPROVAL'
                                    ? 'Pending Management Approval'
                                    : data.status === 'APPROVED'
                                      ? 'Approved'
                                      : data.status}
                          </td>
                          <td>{TimeConverter(data.createdAt)}</td>

                          <td className='dropdown option-dropdown'>
                            <button
                              className='btn border-0 dropdown-toggle'
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                              data-bs-popper-config='{"strategy":"fixed"}'
                            >
                              <svg
                                width='18'
                                height='18'
                                viewBox='0 0 18 18'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M7.49991 8.4705C7.49991 7.72933 8.0955 7.14697 8.82344 7.14697C9.55138 7.14697 10.147 7.74256 10.147 8.4705C10.147 9.19844 9.55138 9.79403 8.82344 9.79403C8.0955 9.79403 7.49991 9.21168 7.49991 8.4705Z'
                                  fill='#707070'
                                />
                                <path
                                  d='M7.49991 14.1175C7.49991 13.3763 8.0955 12.7939 8.82344 12.7939C9.55138 12.7939 10.147 13.3895 10.147 14.1175C10.147 14.8454 9.55138 15.441 8.82344 15.441C8.0955 15.441 7.49991 14.8587 7.49991 14.1175Z'
                                  fill='#707070'
                                />
                                <path
                                  d='M7.49991 2.82353C7.49991 2.08235 8.0955 1.5 8.82344 1.5C9.55138 1.5 10.147 2.09559 10.147 2.82353C10.147 3.55147 9.55138 4.14706 8.82344 4.14706C8.0955 4.14706 7.49991 3.56471 7.49991 2.82353Z'
                                  fill='#707070'
                                />
                              </svg>
                            </button>

                            <ul className='dropdown-menu dropdown-menu-end export-dropdown border-0 shadow'>
                              <li className='dropdown-item'>
                                <Link to={`/admin/loan/${data.id}`}>View Details</Link>
                              </li>
                            </ul>
                          </td>
                        </tr>
                      ))}

                    {loading === false && loan.length === 0 && (
                      <tr>
                        <td colSpan={8} className='text-center'>
                          No loan found
                        </td>
                      </tr>
                    )}
                    {loading === true && loan.length === 0 && (
                      <tr>
                        <td colSpan={8} className='text-center'>
                          <HorizontalLoader></HorizontalLoader>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {paginationData.total >= paginationData.pageSize && (
                <div className='pagination w-100 d-flex justify-content-center'>
                  {paginationData.total && (
                    <Pagination
                      className='pagination-bar ps-0'
                      currentPage={Number(paginationData.pageNumber)}
                      totalCount={paginationData.total}
                      pageSize={paginationData.pageSize}
                      onPageChange={(page: React.SetStateAction<number>) => setPage(page)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoanAdmin
